import { useState, useEffect } from 'react';

export default function HeroSection() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      <div 
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: `url('https://public.readdy.ai/ai/img_res/1669e555cc7c10bf376428a88f378086.jpg')`
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/60"></div>
      </div>

      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-lg' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center">
              <img 
                src="https://static.readdy.ai/image/b4340cdd1673521bdb15ca49ead9cb84/fa617871f52148f2c372440aa483fbed.png" 
                alt="GLOBAL ENERGY SRL Logo" 
                className="h-14 w-auto"
              />
            </div>

            <div className="hidden lg:flex items-center gap-8">
              <button onClick={() => scrollToSection('about')} className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${scrolled ? 'text-gray-700 hover:text-emerald-600' : 'text-white hover:text-emerald-400'}`}>
                Chi Siamo
              </button>
              <button onClick={() => scrollToSection('services')} className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${scrolled ? 'text-gray-700 hover:text-emerald-600' : 'text-white hover:text-emerald-400'}`}>
                Servizi
              </button>
              <button onClick={() => scrollToSection('sectors')} className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${scrolled ? 'text-gray-700 hover:text-emerald-600' : 'text-white hover:text-emerald-400'}`}>
                Settori
              </button>
              <button onClick={() => scrollToSection('why-us')} className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${scrolled ? 'text-gray-700 hover:text-emerald-600' : 'text-white hover:text-emerald-400'}`}>
                Perché Sceglierci
              </button>
              <button onClick={() => scrollToSection('contact')} className={`px-6 py-2.5 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${scrolled ? 'bg-emerald-600 text-white hover:bg-emerald-700' : 'bg-white text-emerald-600 hover:bg-gray-100'}`}>
                Contattaci
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 text-center w-full">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-5xl lg:text-7xl font-bold text-white mb-6 leading-tight">
            Soluzioni Energetiche per il Futuro
          </h2>
          <p className="text-xl lg:text-2xl text-gray-200 mb-12 leading-relaxed">
            Oltre 10 anni di esperienza nel <strong>risparmio energetico</strong>, <strong>pannelli fotovoltaici</strong> e <strong>strutture industriali</strong> ad Arezzo
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button onClick={() => scrollToSection('services')} className="px-8 py-4 bg-emerald-600 text-white rounded-lg text-base font-semibold hover:bg-emerald-700 transition-all inline-flex items-center gap-2 cursor-pointer whitespace-nowrap">
              Scopri i Nostri Servizi
              <i className="ri-arrow-right-line text-xl"></i>
            </button>
            <button onClick={() => scrollToSection('contact')} className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white border-2 border-white rounded-lg text-base font-semibold hover:bg-white hover:text-emerald-600 transition-all cursor-pointer whitespace-nowrap">
              Richiedi Preventivo
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-20 max-w-5xl mx-auto">
          <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20">
            <div className="w-14 h-14 flex items-center justify-center bg-emerald-600 rounded-lg mb-4 mx-auto">
              <i className="ri-time-line text-3xl text-white"></i>
            </div>
            <h3 className="text-2xl font-bold text-white mb-2">10+</h3>
            <p className="text-sm text-gray-200">Anni di Esperienza</p>
          </div>

          <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20">
            <div className="w-14 h-14 flex items-center justify-center bg-emerald-600 rounded-lg mb-4 mx-auto">
              <i className="ri-funds-line text-3xl text-white"></i>
            </div>
            <h3 className="text-2xl font-bold text-white mb-2">€390.000</h3>
            <p className="text-sm text-gray-200">Capitale Sociale</p>
          </div>

          <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20">
            <div className="w-14 h-14 flex items-center justify-center bg-emerald-600 rounded-lg mb-4 mx-auto">
              <i className="ri-map-pin-line text-3xl text-white"></i>
            </div>
            <h3 className="text-2xl font-bold text-white mb-2">Arezzo</h3>
            <p className="text-sm text-gray-200">Sede Operativa</p>
          </div>
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10">
        <button onClick={() => scrollToSection('about')} className="w-10 h-10 flex items-center justify-center border-2 border-white rounded-full text-white hover:bg-white hover:text-emerald-600 transition-all animate-bounce cursor-pointer">
          <i className="ri-arrow-down-line text-xl"></i>
        </button>
      </div>
    </section>
  );
}
